import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { isMobile } from "react-device-detect";
import {
  capitalizeWord,
  HOME_ROUTE,
  APP_ROUTE,
  IN_PLAY_ROUTE,
  useAppSelector,
} from "@hypexone/uicore";
import { getEnvVariable, WhitelabelEnvDTO } from "../../../constants/brandConfig";
import { domain, HOUSE_NAME } from "../../../constants/Branding";
import SkinsPickerPopup, {
  getSkinFavicon,
  redirectToSkin,
  SkinItem,
} from "./SkinsPickerPopup";

const SKINS_RAIL_MAX_VISIBLE_COUNT = 5;
const POSITION_STORAGE_KEY = "skins-rail-movable-position";
const V3_DWELL_MS = 2000;

type RailPosition = { left: number; top: number };
type SkinsRailVersion = "legacy" | "v2" | "v3";

const websiteUrls: WhitelabelEnvDTO["skins"] =
  getEnvVariable(domain, "skins") || [];

const resolveVersion = (): SkinsRailVersion | null => {
  if (HOUSE_NAME === "one8") return "v3";
  if (HOUSE_NAME === "crik18") return "v2";
  if (HOUSE_NAME === "cricket100") return "legacy";
  return null;
};

const readStoredPosition = (): RailPosition | null => {
  try {
    const raw = localStorage.getItem(POSITION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RailPosition;
    if (
      typeof parsed?.left === "number" &&
      typeof parsed?.top === "number" &&
      Number.isFinite(parsed.left) &&
      Number.isFinite(parsed.top)
    ) {
      return parsed;
    }
  } catch {
    // ignore corrupt storage
  }
  return null;
};

const clampPosition = (
  left: number,
  top: number,
  width: number,
  height: number
): RailPosition => {
  const maxLeft = Math.max(0, window.innerWidth - width);
  const maxTop = Math.max(0, window.innerHeight - height);
  return {
    left: Math.min(Math.max(0, left), maxLeft),
    top: Math.min(Math.max(0, top), maxTop),
  };
};

const isSkinsRailVisiblePath = (pathname: string, version: SkinsRailVersion) => {
  const isHome =
    pathname.includes(HOME_ROUTE) ||
    pathname === APP_ROUTE ||
    pathname === "/" ||
    pathname === "/home";

  if (version === "legacy") {
    const isInplayOnMobile = isMobile && pathname.includes(IN_PLAY_ROUTE);
    return isHome || isInplayOnMobile;
  }

  return isHome;
};

const skinItemClass =
  "flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-[10px] border-0 bg-transparent p-0 transition-[background,transform,box-shadow] duration-200 ease-in-out hover:scale-[1.06] hover:bg-white/[0.12] max-[720px]:h-9 max-[720px]:w-9 max-[720px]:rounded-lg";
const skinIconClass =
  "pointer-events-none block h-7 w-7 object-contain max-[720px]:h-6 max-[720px]:w-6";

const SkinsRail: React.FC = () => {
  const version = resolveVersion();
  const movable = version === "v2";
  const { loggedIn } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const railRef = useRef<HTMLElement>(null);
  const positionRef = useRef<RailPosition | null>(
    movable ? readStoredPosition() : null
  );
  const dragStateRef = useRef<{
    pointerId: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);

  const [position, setPosition] = useState<RailPosition | null>(() =>
    movable ? readStoredPosition() : null
  );
  const [isDragging, setIsDragging] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isV3Paused, setIsV3Paused] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const allSkins: SkinItem[] = websiteUrls ?? [];
  const switchableSkins = allSkins.filter((skin) => !skin.disabled);
  const visibleCount =
    version === "v3"
      ? 1
      : Math.min(switchableSkins.length, SKINS_RAIL_MAX_VISIBLE_COUNT);

  useEffect(() => {
    if (version !== "v3") return;
    setActiveIndex(0);
  }, [version, switchableSkins.length]);

  useEffect(() => {
    if (
      version !== "v3" ||
      switchableSkins.length <= 1 ||
      isV3Paused ||
      pickerOpen ||
      !loggedIn
    ) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % switchableSkins.length);
    }, V3_DWELL_MS);

    return () => window.clearInterval(intervalId);
  }, [version, switchableSkins.length, isV3Paused, pickerOpen, loggedIn]);

  const updatePosition = useCallback((next: RailPosition) => {
    positionRef.current = next;
    setPosition(next);
  }, []);

  const persistPosition = useCallback(
    (next: RailPosition) => {
      updatePosition(next);
      try {
        localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore quota / private mode
      }
    },
    [updatePosition]
  );

  const clampCurrentPosition = useCallback(() => {
    const el = railRef.current;
    const current = positionRef.current;
    if (!el || !current) return;
    const rect = el.getBoundingClientRect();
    const next = clampPosition(
      current.left,
      current.top,
      rect.width,
      rect.height
    );
    if (next.left !== current.left || next.top !== current.top) {
      persistPosition(next);
    }
  }, [persistPosition]);

  useEffect(() => {
    if (!movable || !position) return;
    clampCurrentPosition();
    window.addEventListener("resize", clampCurrentPosition);
    return () => window.removeEventListener("resize", clampCurrentPosition);
  }, [movable, position, clampCurrentPosition]);

  const onDragPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!movable || !railRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = railRef.current.getBoundingClientRect();
    const startLeft = positionRef.current?.left ?? rect.left;
    const startTop = positionRef.current?.top ?? rect.top;

    if (!positionRef.current) {
      updatePosition({ left: startLeft, top: startTop });
    }

    dragStateRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - startLeft,
      offsetY: event.clientY - startTop,
    };
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onDragPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragStateRef.current;
    const el = railRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !el) return;

    const next = clampPosition(
      event.clientX - drag.offsetX,
      event.clientY - drag.offsetY,
      el.offsetWidth,
      el.offsetHeight
    );
    updatePosition(next);
  };

  const endDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    if (positionRef.current) {
      persistPosition(positionRef.current);
    }
    dragStateRef.current = null;
    setIsDragging(false);
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // already released
    }
  };

  if (
    !version ||
    !switchableSkins.length ||
    !loggedIn ||
    !isSkinsRailVisiblePath(location.pathname, version)
  ) {
    return null;
  }

  const renderSkinRailItems = (keyPrefix: string, ariaHidden = false) =>
    switchableSkins.map(({ label, domain: skinDomain }) => {
      const skinFavicon = getSkinFavicon(label);

      return (
        <button
          key={`${keyPrefix}-${label}`}
          type="button"
          className={skinItemClass}
          tabIndex={ariaHidden ? -1 : 0}
          title={capitalizeWord(label)}
          aria-label={label}
          aria-hidden={ariaHidden}
          onClick={() => redirectToSkin(skinDomain)}
        >
          {skinFavicon ? (
            <img src={skinFavicon} alt="" className={skinIconClass} />
          ) : null}
        </button>
      );
    });

  const n = switchableSkins.length;
  const railH = visibleCount * 40 + (visibleCount - 1) * 6;
  const railHm = visibleCount * 36 + (visibleCount - 1) * 5;
  const copyH = n * 40 + (n - 1) * 6;
  const placed = Boolean(position);

  if (version === "legacy") {
    return (
      <nav
        className="fixed right-0 top-1/2 z-[12] -translate-y-1/2 rounded-l-[14px] border border-r-0 border-white/10 bg-[rgba(18,18,18,0.72)] px-1 py-1.5 shadow-[-4px_0_20px_rgba(0,0,0,0.25)] backdrop-blur-[10px] max-[720px]:bottom-[40%] max-[720px]:top-auto max-[720px]:translate-y-0 max-[720px]:rounded-l-xl"
        aria-label="Switch site"
      >
        <div
          className="h-[var(--rail-h)] w-full overflow-hidden max-[720px]:h-[var(--rail-h-m)]"
          style={
            {
              "--rail-h": `${railH}px`,
              "--rail-h-m": `${railHm}px`,
            } as React.CSSProperties
          }
        >
          <div
            className="flex flex-col animate-skinsRailScroll will-change-transform hover:[animation-play-state:paused] motion-reduce:animate-none"
            style={{ animationDuration: `${(copyH / 224) * 12}s` }}
          >
            <div className="flex flex-col items-center gap-1.5 p-0 max-[720px]:gap-[5px]">
              {renderSkinRailItems("a")}
            </div>
            <div
              className="flex flex-col items-center gap-1.5 p-0 max-[720px]:gap-[5px]"
              aria-hidden="true"
            >
              {renderSkinRailItems("b", true)}
            </div>
          </div>
        </div>
      </nav>
    );
  }

  const activeSkin = switchableSkins[activeIndex] ?? switchableSkins[0];
  const activeFavicon = activeSkin ? getSkinFavicon(activeSkin.label) : null;

  const navClass = [
    "fixed z-[12] flex touch-none select-none flex-col items-center",
    version === "v3"
      ? "group right-2.5 top-[42%] -translate-y-1/2 gap-0 overflow-visible rounded-[18px] border border-white/[0.21] bg-[linear-gradient(160deg,rgba(14,28,48,0.94)_0%,rgba(8,16,30,0.92)_100%)] px-[7px] pb-[7px] pt-2 shadow-[0_10px_28px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-[14px] touch-manipulation max-[720px]:bottom-[41%] max-[720px]:right-[5px] max-[720px]:top-auto max-[720px]:translate-y-0 max-[720px]:rounded-2xl max-[720px]:px-1 max-[720px]:py-[5px]"
      : "right-0 top-1/2 -translate-y-1/2 gap-1 border border-white/10 border-r-0 bg-[rgba(18,18,18,0.72)] px-1 py-1.5 shadow-[-4px_0_20px_rgba(0,0,0,0.25)] backdrop-blur-[10px] max-[720px]:bottom-[40%] max-[720px]:top-auto max-[720px]:translate-y-0",
    version === "v2" && !placed && !isDragging
      ? "rounded-l-[14px] max-[720px]:rounded-l-xl"
      : "",
    version === "v2" && (placed || isDragging)
      ? "rounded-[14px] border-r border-white/10 shadow-[0_8px_28px_rgba(0,0,0,0.35)] max-[720px]:rounded-xl"
      : "",
    isDragging ? "cursor-grabbing opacity-[0.92]" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const placedStyle: React.CSSProperties | undefined = placed
    ? {
        left: position!.left,
        top: position!.top,
        right: "auto",
        bottom: "auto",
        transform: "none",
      }
    : undefined;

  return (
    <>
      <nav
        ref={railRef}
        className={navClass}
        aria-label="Switch site"
        style={placedStyle}
        onMouseEnter={
          version === "v3" ? () => setIsV3Paused(true) : undefined
        }
        onMouseLeave={
          version === "v3" ? () => setIsV3Paused(false) : undefined
        }
      >
        <div
          className={
            version === "v3"
              ? "w-full overflow-visible [perspective:520px] [perspective-origin:50%_50%]"
              : "h-[var(--rail-h)] w-full overflow-hidden max-[720px]:h-[var(--rail-h-m)]"
          }
          style={
            version === "v3"
              ? undefined
              : ({
                  "--rail-h": `${railH}px`,
                  "--rail-h-m": `${railHm}px`,
                } as React.CSSProperties)
          }
        >
          {version === "v3" ? (
            <div className="flex flex-col items-center [transform-style:preserve-3d]">
              {activeSkin ? (
                <button
                  key={activeIndex}
                  type="button"
                  className="flex w-[54px] cursor-pointer flex-col items-center gap-1.5 border-0 bg-transparent p-0 text-white max-[720px]:w-12"
                  title={capitalizeWord(activeSkin.label)}
                  onClick={() => setPickerOpen(true)}
                  aria-label="Open skins"
                >
                  <span className="relative flex h-[46px] w-[46px] items-center justify-center rounded-full bg-white/[0.04] shadow-[0_4px_12px_rgba(0,0,0,0.35)] before:pointer-events-none before:absolute before:inset-0 before:rounded-full before:bg-[var(--ion-color-primary)] before:p-0.5 before:content-[''] before:[mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)] before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)] before:[mask-composite:exclude] before:[-webkit-mask-composite:xor] max-[720px]:h-[42px] max-[720px]:w-[42px]">
                    <span
                      key={`icon-${activeIndex}`}
                      className="relative z-[1] flex h-[34px] w-[34px] items-center justify-center overflow-hidden rounded-[10px] bg-white/[0.06] [transform-origin:center] [transform-style:preserve-3d] [backface-visibility:hidden] animate-skinsRailV3Pop will-change-[transform,filter] group-hover:[animation-play-state:paused] motion-reduce:animate-none max-[720px]:h-[30px] max-[720px]:w-[30px] max-[720px]:rounded-lg"
                    >
                      {activeFavicon ? (
                        <img
                          src={activeFavicon}
                          alt=""
                          className="pointer-events-none block h-7 w-7 object-contain max-[720px]:h-6 max-[720px]:w-6"
                        />
                      ) : null}
                    </span>
                  </span>
                </button>
              ) : null}
            </div>
          ) : (
            <div className="flex flex-col animate-skinsRailScroll will-change-transform hover:[animation-play-state:paused] motion-reduce:animate-none">
              <div className="flex flex-col items-center gap-1.5 p-0 max-[720px]:gap-[5px]">
                {renderSkinRailItems("a")}
              </div>
              <div
                className="flex flex-col items-center gap-1.5 p-0 max-[720px]:gap-[5px]"
                aria-hidden="true"
              >
                {renderSkinRailItems("b", true)}
              </div>
            </div>
          )}
        </div>

        {movable ? (
          <button
            type="button"
            className="flex w-full min-h-[14px] cursor-grab touch-none items-center justify-center border-0 bg-transparent px-0 pb-0.5 pt-1 active:cursor-grabbing"
            aria-label="Move skins rail"
            title="Drag to move"
            onPointerDown={onDragPointerDown}
            onPointerMove={onDragPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            <span
              className="block h-2.5 w-[18px] rounded-sm opacity-85 bg-[repeating-linear-gradient(to_bottom,rgba(255,255,255,0.45)_0_2px,transparent_2px_4px)]"
              aria-hidden="true"
            />
          </button>
        ) : null}
      </nav>

      <SkinsPickerPopup
        open={version === "v3" && pickerOpen}
        onClose={() => setPickerOpen(false)}
        skins={allSkins}
      />
    </>
  );
};

export default SkinsRail;
